package features.booking

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import kotlinx.coroutines.launch
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/** Una reserva/viaje cancelado pendiente de reembolso o reasignación. */
data class CancelacionRegistro(
    val id: String = "",
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val reservaId: String = "",
    val pasajeros: String = "",
    val correo: String = "",
    val telefono: String = "",
    val totalReembolsar: Int = 0,
    val canceladoPorNombre: String = "",
    val origen: String = "",
    val estadoResolucion: String = "pendiente", // pendiente | reembolsado | reasignado
    val resueltoPorNombre: String = "",
    val reasignadoA: String = "", // "ruta | fecha | horario" del viaje destino
    val fechaCancelacion: Long = 0L,
    val fechaResolucion: Long = 0L,
    val asientosOriginales: List<Int> = emptyList()
)

/** Un viaje programado disponible como destino para reasignar. */
data class ViajeDisponibleReasignacion(
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val capacidad: Int = 0,
    val ocupados: Int = 0
) {
    val libres: Int get() = (capacidad - ocupados).coerceAtLeast(0)
}

/** Resultado de una reasignación (para mostrar mensaje al admin). */
data class ReasignacionResultado(val ok: Boolean, val mensaje: String)

private fun estiloEstado(estado: String): Triple<String, Color, androidx.compose.ui.graphics.vector.ImageVector> = when (estado) {
    "reembolsado" -> Triple("Reembolsado", SmartVanColors.VerdeOscuro, Icons.Filled.Payments)
    "reasignado" -> Triple("Reasignado", Color(0xFF1565C0), Icons.Filled.SwapHoriz)
    "archivado" -> Triple("Archivado", Color.Gray, Icons.Filled.Info)
    else -> Triple("Pendiente", SmartVanColors.NaranjaDeep, Icons.Filled.HourglassEmpty)
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CancelacionesView(viewModel: AdminViewModel) {
    val cancelaciones by viewModel.cancelaciones.collectAsState()
    val cargando by viewModel.cargandoCancelaciones.collectAsState()
    var filtro by remember { mutableStateOf("Pendientes") }
    var busqueda by remember { mutableStateOf("") }
    var reasignando by remember { mutableStateOf<CancelacionRegistro?>(null) }
    var resultadoReasignar by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) { viewModel.cargarCancelaciones() }

    val filtrados = cancelaciones.filter { c ->
        val matchFiltro = when (filtro) {
            "Pendientes" -> c.estadoResolucion == "pendiente"
            "Resueltas" -> c.estadoResolucion != "pendiente"
            else -> true
        }
        val matchBusqueda = busqueda.isBlank() ||
            c.pasajeros.contains(busqueda, ignoreCase = true) ||
            c.ruta.contains(busqueda, ignoreCase = true) ||
            c.reservaId.contains(busqueda, ignoreCase = true)
        matchFiltro && matchBusqueda
    }

    val pendientes = cancelaciones.filter { it.estadoResolucion == "pendiente" }
    val totalPendiente = pendientes.sumOf { it.totalReembolsar }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
            Icon(Icons.Filled.MoneyOff, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(28.dp))
            Spacer(modifier = Modifier.width(8.dp))
            Text("Cancelaciones y reembolsos", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            Spacer(modifier = Modifier.weight(1f))
            IconButton(onClick = { viewModel.cargarCancelaciones() }) {
                Icon(Icons.Filled.Refresh, contentDescription = "Actualizar", tint = SmartVanColors.CyanAccent)
            }
        }
        Text("Quién canceló, cuánto reembolsar y a quién reasignar. Marca cada caso al resolverlo.",
            color = Color.Gray, style = MaterialTheme.typography.bodySmall)
        Spacer(modifier = Modifier.height(12.dp))

        // Resumen pendiente
        if (pendientes.isNotEmpty()) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.NaranjaDeep.copy(alpha = 0.10f)),
                border = BorderStroke(1.dp, SmartVanColors.NaranjaDeep.copy(alpha = 0.4f))
            ) {
                Row(modifier = Modifier.fillMaxWidth().padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.HourglassEmpty, contentDescription = null, tint = SmartVanColors.NaranjaDeep, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("${pendientes.size} pendiente(s) por resolver", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Spacer(modifier = Modifier.weight(1f))
                    Text("$$totalPendiente MXN", fontWeight = FontWeight.Black, color = SmartVanColors.NaranjaDeep)
                }
            }
            Spacer(modifier = Modifier.height(10.dp))
        }

        OutlinedTextField(
            value = busqueda,
            onValueChange = { busqueda = it },
            modifier = Modifier.fillMaxWidth(),
            placeholder = { Text("Buscar por pasajero, ruta o reserva...") },
            leadingIcon = { Icon(Icons.Filled.Search, contentDescription = null, tint = Color.Gray) },
            shape = RoundedCornerShape(12.dp),
            singleLine = true
        )
        Spacer(modifier = Modifier.height(8.dp))

        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            listOf("Pendientes", "Resueltas", "Todas").forEach { op ->
                FilterChip(
                    selected = filtro == op,
                    onClick = { filtro = op },
                    label = { Text(op) }
                )
            }
        }
        Spacer(modifier = Modifier.height(12.dp))

        if (cargando) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
            }
        } else if (filtrados.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                Text("Sin cancelaciones con estos filtros.", color = Color.Gray, fontStyle = FontStyle.Italic)
            }
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                items(filtrados, key = { it.id }) { c ->
                    CancelacionCard(
                        c = c,
                        onReembolsar = { viewModel.marcarResolucionCancelacion(c.id, "reembolsado") },
                        onArchivar = { viewModel.marcarResolucionCancelacion(c.id, "archivado") },
                        onReasignar = { reasignando = c }
                    )
                }
            }
        }
    }

    reasignando?.let { c ->
        DialogoReasignacion(
            cancelacion = c,
            viewModel = viewModel,
            onCerrar = { reasignando = null },
            // Tras reasignar: mostrar a dónde fue y saltar a "Resueltas" para que la tarjeta
            // reasignada (con origen → destino) quede visible en vez de desaparecer.
            onReasignado = { msg -> resultadoReasignar = msg; filtro = "Resueltas" }
        )
    }

    resultadoReasignar?.let { msg ->
        AlertDialog(
            onDismissRequest = { resultadoReasignar = null },
            icon = { Icon(Icons.Filled.SwapHoriz, contentDescription = null, tint = Color(0xFF1565C0)) },
            title = { Text("Reasignación lista") },
            text = { Text(msg) },
            confirmButton = {
                Button(
                    onClick = { resultadoReasignar = null },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1565C0))
                ) { Text("Entendido") }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DialogoReasignacion(cancelacion: CancelacionRegistro, viewModel: AdminViewModel, onCerrar: () -> Unit, onReasignado: (String) -> Unit) {
    val viajes by viewModel.viajesDisponibles.collectAsState()
    val cargando by viewModel.cargandoViajesDisponibles.collectAsState()
    var grupoAbierto by remember { mutableStateOf<String?>(null) }
    var procesando by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    // Selector de asiento cuando el original no está libre
    var pickerViaje by remember { mutableStateOf<ViajeDisponibleReasignacion?>(null) }
    var pickerCapacidad by remember { mutableStateOf(0) }
    var pickerLibres by remember { mutableStateOf<List<Int>>(emptyList()) }
    var pickerSeleccion by remember { mutableStateOf<List<Int>>(emptyList()) }

    LaunchedEffect(Unit) { viewModel.cargarViajesDisponibles() }

    // Agrupar por "ruta · fecha"; cada grupo lista sus horarios
    val grupos = viajes.groupBy { "${it.ruta}  ·  ${it.fecha}" }
    val titulosOrdenados = grupos.keys.sorted()

    AlertDialog(
        onDismissRequest = { if (!procesando) onCerrar() },
        title = { Text("Reasignar viaje", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro) },
        text = {
            Column(modifier = Modifier.fillMaxWidth().heightIn(max = 460.dp)) {
                Text("Pasajero(s): ${cancelacion.pasajeros.ifBlank { "—" }}", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                Text("Elige el viaje destino. Al confirmar se envía el nuevo boleto, QR y horario por correo.",
                    style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                Spacer(modifier = Modifier.height(10.dp))

                if (procesando) {
                    Box(modifier = Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Reasignando y enviando correo…", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                        }
                    }
                } else if (cargando) {
                    Box(modifier = Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
                    }
                } else if (grupos.isEmpty()) {
                    Text("No hay viajes programados disponibles (de hoy en adelante).", color = Color.Gray, fontStyle = FontStyle.Italic)
                } else {
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        titulosOrdenados.forEach { titulo ->
                            val lista = grupos[titulo].orEmpty()
                            item(key = titulo) {
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(10.dp),
                                    border = BorderStroke(1.dp, SmartVanColors.AzulOscuro.copy(alpha = 0.25f)),
                                    colors = CardDefaults.cardColors(containerColor = Color.White)
                                ) {
                                    Column(modifier = Modifier.fillMaxWidth().padding(10.dp)) {
                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            modifier = Modifier.fillMaxWidth().clickable {
                                                grupoAbierto = if (grupoAbierto == titulo) null else titulo
                                            }
                                        ) {
                                            Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(18.dp))
                                            Spacer(modifier = Modifier.width(6.dp))
                                            Text(titulo, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
                                            Spacer(modifier = Modifier.weight(1f))
                                            Icon(
                                                if (grupoAbierto == titulo) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                                                contentDescription = null, tint = Color.Gray
                                            )
                                        }
                                        if (grupoAbierto == titulo) {
                                            Spacer(modifier = Modifier.height(6.dp))
                                            lista.sortedBy { it.horario }.forEach { v ->
                                                val sinCupo = v.libres <= 0
                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp)
                                                ) {
                                                    Icon(Icons.Filled.Schedule, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(15.dp))
                                                    Spacer(modifier = Modifier.width(5.dp))
                                                    Text(v.horario, color = Color.DarkGray, style = MaterialTheme.typography.bodyMedium)
                                                    Spacer(modifier = Modifier.width(8.dp))
                                                    Text(
                                                        if (sinCupo) "Sin cupo" else "${v.libres} libre(s)",
                                                        color = if (sinCupo) Color.Red else SmartVanColors.VerdeOscuro,
                                                        fontSize = 12.sp
                                                    )
                                                    Spacer(modifier = Modifier.weight(1f))
                                                    Button(
                                                        onClick = {
                                                            procesando = true
                                                            scope.launch {
                                                                val (cap, libres) = viewModel.asientosLibresDeViaje(v.ruta, v.fecha, v.horario)
                                                                val originales = cancelacion.asientosOriginales.filter { it > 0 }
                                                                val cantidad = cancelacion.asientosOriginales.size
                                                                when {
                                                                    cantidad == 0 || libres.size < cantidad -> {
                                                                        // sin datos o sin cupo → intentar directo (backend valida/mensaje)
                                                                        viewModel.reasignarCancelacion(cancelacion.id, v.ruta, v.fecha, v.horario, null) { res ->
                                                                            procesando = false; if (res.ok) { onReasignado(res.mensaje); onCerrar() }
                                                                        }
                                                                    }
                                                                    originales.isNotEmpty() && originales.all { it in libres } -> {
                                                                        // Mantener el/los asiento(s) original(es) — sin fricción
                                                                        viewModel.reasignarCancelacion(cancelacion.id, v.ruta, v.fecha, v.horario, null) { res ->
                                                                            procesando = false; if (res.ok) { onReasignado(res.mensaje); onCerrar() }
                                                                        }
                                                                    }
                                                                    else -> {
                                                                        // Original ocupado → abrir selector de asiento
                                                                        procesando = false
                                                                        pickerCapacidad = cap
                                                                        pickerLibres = libres
                                                                        pickerSeleccion = originales.filter { it in libres }
                                                                        pickerViaje = v
                                                                    }
                                                                }
                                                            }
                                                        },
                                                        enabled = !sinCupo,
                                                        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                                        colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde)
                                                    ) { Text("Mover aquí", fontSize = 12.sp) }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = { if (!procesando) onCerrar() }) { Text("Cerrar", color = Color.Gray) }
        }
    )

    // Selector de asiento (cuando el original está ocupado en el viaje destino)
    val pickerLayout by viewModel.layoutViaje.collectAsState()
    LaunchedEffect(pickerViaje?.ruta, pickerViaje?.fecha, pickerViaje?.horario) {
        pickerViaje?.let { viewModel.cargarLayoutDeViaje(it.ruta, it.fecha, it.horario) }
    }
    pickerViaje?.let { v ->
        val cantidad = cancelacion.asientosOriginales.size
        // ocupados = todos los asientos del vehículo que NO están en la lista de libres.
        // Los números salen del layout real: la numeración es manual y puede no ser 1..capacidad.
        val todosLosAsientos = pickerLayout?.filas
            ?.flatMap { f -> f.asientos.filter { it > 0 } }?.distinct()?.sorted()
            ?: (1..pickerCapacidad).toList()
        val ocupadosSet = todosLosAsientos.filter { it !in pickerLibres }.toSet()
        AlertDialog(
            onDismissRequest = { if (!procesando) pickerViaje = null },
            title = { Text("Elegir asiento", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro) },
            text = {
                Column(modifier = Modifier.fillMaxWidth().heightIn(max = 460.dp).verticalScroll(rememberScrollState())) {
                    Text("${v.ruta} · ${v.fecha} · ${v.horario}", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                    Text(
                        "Asiento original ${cancelacion.asientosOriginales.joinToString(", ").ifBlank { "—" }} ocupado. Elige $cantidad libre(s).",
                        style = MaterialTheme.typography.bodySmall, color = Color.Gray
                    )
                    Spacer(modifier = Modifier.height(10.dp))
                    MapaAsientosVehiculo(
                        vehiculo = pickerLayout,
                        ocupados = ocupadosSet,
                        seleccionados = pickerSeleccion.toSet(),
                        onToggleAsiento = { num ->
                            val sel = num in pickerSeleccion
                            pickerSeleccion = when {
                                sel -> pickerSeleccion - num
                                pickerSeleccion.size < cantidad -> pickerSeleccion + num
                                else -> pickerSeleccion
                            }
                        }
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        "Seleccionados: ${pickerSeleccion.sorted().joinToString(", ").ifBlank { "ninguno" }} ($cantidad requerido(s))",
                        style = MaterialTheme.typography.bodySmall, color = SmartVanColors.AzulOscuro, fontWeight = FontWeight.SemiBold
                    )
                    if (procesando) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp, color = SmartVanColors.AzulOscuro)
                            Spacer(modifier = Modifier.width(6.dp)); Text("Reasignando…", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        procesando = true
                        viewModel.reasignarCancelacion(cancelacion.id, v.ruta, v.fecha, v.horario, pickerSeleccion.sorted()) { res ->
                            procesando = false
                            if (res.ok) { onReasignado(res.mensaje); pickerViaje = null; onCerrar() }
                        }
                    },
                    enabled = pickerSeleccion.size == cantidad && !procesando,
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde)
                ) { Text("Confirmar") }
            },
            dismissButton = { TextButton(onClick = { if (!procesando) pickerViaje = null }) { Text("Cancelar", color = Color.Gray) } }
        )
    }
}

// Arma enlace wa.me con el número del cliente (asume México si trae 10 dígitos) + mensaje prellenado.
internal fun construirWhatsAppUrl(telefono: String, mensaje: String): String {
    val digits = telefono.filter { it.isDigit() }
    val numero = if (digits.length == 10) "52$digits" else digits
    val texto = mensaje.encodeToByteArray().joinToString("") { b ->
        val i = b.toInt() and 0xFF
        val ch = i.toChar()
        if (i < 128 && (ch in 'A'..'Z' || ch in 'a'..'z' || ch in '0'..'9')) ch.toString()
        else "%" + i.toString(16).uppercase().padStart(2, '0')
    }
    return "https://wa.me/$numero?text=$texto"
}

@Composable
private fun CancelacionCard(c: CancelacionRegistro, onReembolsar: () -> Unit, onArchivar: () -> Unit, onReasignar: () -> Unit) {
    val (etiqueta, color, icono) = estiloEstado(c.estadoResolucion)
    val resuelto = c.estadoResolucion != "pendiente"
    var confirmandoReembolso by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, color.copy(alpha = 0.3f)),
        colors = CardDefaults.cardColors(containerColor = Color.White)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                Icon(icono, contentDescription = null, tint = color, modifier = Modifier.size(18.dp))
                Spacer(modifier = Modifier.width(6.dp))
                Text(etiqueta, fontWeight = FontWeight.Bold, color = color, style = MaterialTheme.typography.bodyMedium)
                if (c.origen == "viaje_completo") {
                    Spacer(modifier = Modifier.width(6.dp))
                    Text("• Viaje completo", color = Color.Gray, fontSize = 11.sp)
                }
                Spacer(modifier = Modifier.weight(1f))
                if (c.totalReembolsar > 0) Text("$${c.totalReembolsar} MXN", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            }
            Spacer(modifier = Modifier.height(6.dp))
            if (c.pasajeros.isNotBlank()) {
                Row(verticalAlignment = Alignment.Top) {
                    Icon(Icons.Filled.Person, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(c.pasajeros, fontWeight = FontWeight.Medium, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall)
                }
            }
            if (c.correo.isNotBlank()) {
                Text(c.correo, color = Color.Gray, style = MaterialTheme.typography.bodySmall)
            }
            if (c.telefono.isNotBlank()) {
                Spacer(modifier = Modifier.height(4.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Phone, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(14.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(c.telefono, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                    OutlinedButton(
                        onClick = {
                            val msg = "Hola, le escribo de SmartVan sobre su viaje ${c.ruta} del ${c.fecha} ${c.horario}. Necesitamos coordinar el reembolso o la reasignacion de su lugar."
                            uriHandler.openUri(construirWhatsAppUrl(c.telefono, msg))
                        },
                        modifier = Modifier.height(30.dp),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 0.dp),
                        border = BorderStroke(1.dp, Color(0xFF25D366))
                    ) {
                        Icon(Icons.Filled.Chat, contentDescription = null, tint = Color(0xFF25D366), modifier = Modifier.size(13.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("WhatsApp", fontSize = 11.sp, color = Color(0xFF25D366))
                    }
                }
            }
            if (c.ruta.isNotBlank()) {
                Text("${c.ruta} • ${c.fecha} ${c.horario}".trim(), color = Color.Gray, style = MaterialTheme.typography.bodySmall)
            }
            if (c.canceladoPorNombre.isNotBlank()) {
                Text("Canceló: ${c.canceladoPorNombre}", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
            }
            if (c.fechaCancelacion > 0L) {
                Text(org.example.abigail.formatMillisToDate(c.fechaCancelacion), color = Color.Gray, style = MaterialTheme.typography.labelSmall)
            }

            if (resuelto) {
                Spacer(modifier = Modifier.height(4.dp))
                val porQuien = c.resueltoPorNombre.ifBlank { "—" }
                if (c.estadoResolucion == "reasignado") {
                    val origenViaje = "${c.ruta} • ${c.fecha} ${c.horario}".trim()
                    val partes = c.reasignadoA.split(" | ")
                    val destinoViaje = if (partes.size >= 3) "${partes[0]} • ${partes[1]} ${partes[2]}" else c.reasignadoA.ifBlank { "—" }
                    Text(
                        "Reasignado: $origenViaje  →  $destinoViaje",
                        color = color, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.labelSmall
                    )
                    Text("Reasignado por $porQuien", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                } else if (c.estadoResolucion == "archivado") {
                    Text(
                        "Archivado (no-show) por $porQuien",
                        color = color, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.labelSmall
                    )
                } else {
                    Text(
                        "Reembolsado por $porQuien",
                        color = color, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.labelSmall
                    )
                }
            } else {
                Spacer(modifier = Modifier.height(10.dp))
                // No-show: el pasajero en efectivo no pagó y el viaje ya pasó. NO es reasignable
                // (no hay nada que mover a futuro) ni reembolsable (nunca pagó). Solo se archiva.
                val esNoShow = c.origen == "efectivo_no_show"
                if (esNoShow) {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja),
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Info, contentDescription = null, tint = SmartVanColors.NaranjaDeep, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                "No-show: reserva en efectivo sin pagar de un viaje ya pasado. No se reasigna ni reembolsa; solo archívala.",
                                color = SmartVanColors.NaranjaDeep, style = MaterialTheme.typography.labelSmall,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))
                    OutlinedButton(
                        onClick = { onArchivar() },   // no-show: se archiva (NO se marca como reembolsado)
                        modifier = Modifier.fillMaxWidth()
                    ) { Text("Archivar (visto)", fontSize = 13.sp, color = SmartVanColors.NaranjaDeep) }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = { confirmandoReembolso = true },
                            modifier = Modifier.weight(1f),
                            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.VerdeOscuro)
                        ) { Text("Reembolsado", fontSize = 13.sp) }
                        OutlinedButton(
                            onClick = onReasignar,
                            modifier = Modifier.weight(1f)
                        ) { Text("Reasignar", fontSize = 13.sp, color = Color(0xFF1565C0)) }
                    }
                }
            }
        }
    }

    if (confirmandoReembolso) {
        AlertDialog(
            onDismissRequest = { confirmandoReembolso = false },
            icon = { Icon(Icons.Filled.Payments, contentDescription = null, tint = SmartVanColors.VerdeOscuro) },
            title = { Text("Confirmar reembolso") },
            text = {
                Column {
                    Text("Vas a marcar esta cancelación como REEMBOLSADA. Quedará en el historial (filtro \"Resueltas\") a tu nombre y no se podrá volver a marcar.")
                    Spacer(modifier = Modifier.height(8.dp))
                    if (c.pasajeros.isNotBlank()) Text("Pasajero(s): ${c.pasajeros}", fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.bodySmall)
                    if (c.totalReembolsar > 0) Text("Monto a reembolsar: $${c.totalReembolsar} MXN", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    if (c.ruta.isNotBlank()) Text("${c.ruta} • ${c.fecha} ${c.horario}".trim(), color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                }
            },
            confirmButton = {
                Button(
                    onClick = { confirmandoReembolso = false; onReembolsar() },
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.VerdeOscuro)
                ) { Text("Sí, reembolsado") }
            },
            dismissButton = {
                TextButton(onClick = { confirmandoReembolso = false }) { Text("Cancelar", color = Color.Gray) }
            }
        )
    }
}
